/**
 * The widget's two AudioWorklet modules, and how they are loaded.
 *
 * The widget runs on other people's pages, under their Content-Security-Policy.
 * `audioWorklet.addModule` is governed by `script-src`, so each module is
 * loaded first from the widget's own origin — the build writes these sources
 * to `dist/` and the API serves them next to the bundle at `/widget/v1/` (CORS
 * is open there) — which any host that can load the script tag already allows.
 * Only when that is refused (an npm build whose host does not allow the API
 * origin, a self-hosted bundle without the files) does it fall back to a
 * `blob:` URL of the same source, which needs `blob:` in `script-src`.
 * docs/embed.md has the host-side CSP.
 *
 * The sources are plain JS strings so that one definition serves both the
 * built files (tsup.config.ts writes them) and the fallback.
 */

/** The voice socket's mic: posts mono Float32 blocks (~128 ms at 16 kHz). */
export const MIC_WORKLET_SOURCE = `
class DeuteroMicProcessor extends AudioWorkletProcessor {
  constructor() { super(); this.buf = []; this.len = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) {
      this.buf.push(ch.slice(0));
      this.len += ch.length;
      if (this.len >= 2048) {
        const out = new Float32Array(this.len);
        let off = 0;
        for (const part of this.buf) { out.set(part, off); off += part.length; }
        this.port.postMessage(out, [out.buffer]);
        this.buf = []; this.len = 0;
      }
    }
    return true;
  }
}
registerProcessor("deutero-mic", DeuteroMicProcessor);
`;

/**
 * The continuous recorder's worklet — the twin of the consoles'
 * webui/static/shared/recorderWorklet.js (keep them in step). Two inputs, each
 * downmixed to mono: 0 the participant's mic, 1 the interviewer's TTS tap. It
 * posts {frame, time, p, i} about every 100 ms; `frame` is the page clock of
 * the batch's first sample (from 0 at the first process(), never reset), and
 * `time` that sample's context time. An input with nothing connected is
 * silence, posted as zeros so both sides stay sample-aligned. A render quantum
 * that overruns the batch carries its remainder into the next one, so every
 * frame is posted exactly once.
 */
export const RECORDER_WORKLET_SOURCE = `
const QUANTUM = 128;

function downmix(input, from, out, offset, n) {
  const channels = input ? input.length : 0;
  if (!channels) return;
  if (channels === 1) { out.set(input[0].subarray(from, from + n), offset); return; }
  for (let k = 0; k < n; k++) {
    let sum = 0;
    for (let c = 0; c < channels; c++) sum += input[c][from + k];
    out[offset + k] = sum / channels;
  }
}

class DeuteroRecorder extends AudioWorkletProcessor {
  constructor() {
    super();
    this.frame = 0;
    this.batchFrames = Math.max(QUANTUM, Math.round(sampleRate / 10 / QUANTUM) * QUANTUM);
    this.#fresh();
  }

  #fresh() {
    this.p = new Float32Array(this.batchFrames);
    this.i = new Float32Array(this.batchFrames);
    this.fill = 0;
    this.start = this.frame;
    this.time = currentTime;
  }

  process(inputs) {
    const n = (inputs[0] && inputs[0][0] && inputs[0][0].length)
      || (inputs[1] && inputs[1][0] && inputs[1][0].length)
      || QUANTUM;
    let used = 0;
    while (used < n) {
      if (this.fill === 0) {
        this.start = this.frame;
        this.time = currentTime + used / sampleRate;
      }
      const take = Math.min(n - used, this.batchFrames - this.fill);
      downmix(inputs[0], used, this.p, this.fill, take);
      downmix(inputs[1], used, this.i, this.fill, take);
      this.fill += take;
      this.frame += take;
      used += take;
      if (this.fill >= this.batchFrames) {
        this.port.postMessage(
          { frame: this.start, time: this.time, p: this.p, i: this.i },
          [this.p.buffer, this.i.buffer],
        );
        this.#fresh();
      }
    }
    return true;
  }
}

registerProcessor("deutero-recorder", DeuteroRecorder);
`;

/** File names under `dist/` (and so under `/widget/v1/`). */
export const WORKLET_FILES: Record<string, string> = {
  "mic-worklet.js": MIC_WORKLET_SOURCE,
  "recorder-worklet.js": RECORDER_WORKLET_SOURCE,
};

/** Where the widget's own copy of a worklet file lives: next to the bundle,
 *  on the API origin. Null without a base (nothing to try but the fallback). */
export function workletUrl(apiBase: string | null | undefined, file: string): string | null {
  if (!apiBase) return null;
  return `${apiBase.replace(/\/+$/, "")}/widget/v1/${file}`;
}

/** Add a worklet module: from `url` when given, else (or when that is
 *  refused) from a blob: URL of `source`. Rejects only if both fail. */
export async function loadWorklet(
  ctx: BaseAudioContext,
  url: string | null,
  source: string
): Promise<void> {
  if (url) {
    try {
      await ctx.audioWorklet.addModule(url);
      return;
    } catch {
      /* refused by the host's CSP, or not served: try the inline copy */
    }
  }
  const blobUrl = URL.createObjectURL(new Blob([source], { type: "application/javascript" }));
  try {
    await ctx.audioWorklet.addModule(blobUrl);
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}
